import { useState, useRef, useCallback, type TouchEvent as ReactTouchEvent } from 'react';

interface SwipeConfig {
  threshold?: number;
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
}

export function useSwipeGesture(config: SwipeConfig = {}) {
  const { threshold = 80, onSwipeLeft, onSwipeRight } = config;
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const [isSwiping, setIsSwiping] = useState(false);
  const [swipeOffset, setSwipeOffset] = useState(0);

  const handleTouchStart = useCallback(
    (e: ReactTouchEvent) => {
      touchStartX.current = e.touches[0].clientX;
      touchStartY.current = e.touches[0].clientY;
      setIsSwiping(true);
      setSwipeOffset(0);
    },
    [],
  );

  const handleTouchMove = useCallback(
    (e: ReactTouchEvent) => {
      if (!isSwiping) return;
      const deltaX = e.touches[0].clientX - touchStartX.current;
      const deltaY = e.touches[0].clientY - touchStartY.current;

      if (Math.abs(deltaX) > Math.abs(deltaY)) {
        e.preventDefault();
        setSwipeOffset(deltaX);
      }
    },
    [isSwiping],
  );

  const handleTouchEnd = useCallback(() => {
    if (!isSwiping) return;
    setIsSwiping(false);

    if (Math.abs(swipeOffset) >= threshold) {
      if (swipeOffset < 0 && onSwipeLeft) onSwipeLeft();
      if (swipeOffset > 0 && onSwipeRight) onSwipeRight();
    }
    setSwipeOffset(0);
  }, [isSwiping, swipeOffset, threshold, onSwipeLeft, onSwipeRight]);

  return {
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    isSwiping,
    swipeOffset,
  };
}